<template>
	<div class="MultiselectMobile">
		<button
			class="BackButton"
			aria-label="Close Multiselect"
			@click="$emit('close')"
		>
			<i class="lnr-chevron-left" aria-hidden="true"></i>
		</button>
		<div class="Multiselect">
			<Multiselect
				ref="multiselectRef"
				v-model="internalValue"
				:mode="props.mode"
				:options="formattedOptions"
				:placeholder="props.placeholder"
				:disabled="props.disabled"
				:searchable="props.searchable"
				:close-on-select="false"
				:can-clear="props.canClear"
				:hide-selected="props.mode === MultiselectMode.SINGLE"
				:caret="false"
				:open-direction="'bottom'"
				:no-options-text="props.noOptionsText"
				:no-results-text="props.noResultsText"
				class="MultiselectDropdown isAlwaysOpen"
			>
				<template #option="{ option, isSelected }">
					<div class="SelectItemWrapper">
						<SelectItem
							:item="mapOptionToSelectItem(option, isSelected(option))"
							:type="selectItemType"
						>
							<template v-if="option.icon" #icon>
								<i :class="`Icon ${option.icon}`" aria-hidden="true"></i>
							</template>
						</SelectItem>
					</div>
				</template>

				<template #beforelist>
					<p v-if="props.description" class="MultiselectDescription">{{ props.description }}</p>
					<p v-if="props.warning" class="MultiselectWarning">{{ props.warning }}</p>

					<div v-if="props.isLocationFilter" class="ContinentChange">
						<span class="ContinentLabel">Continent</span>
						<button type="button" class="ChangeButton" @click="$emit('changeContinent')">
							Change
						</button>
					</div>
				</template>

				<template #singlelabel="{ value }">
					<div class="MultiselectSingleLabel">
						<i v-if="value.icon" :class="`Icon ${value.icon}`" aria-hidden="true"></i>
						<span>{{ value.label }}</span>
					</div>
				</template>

				<template v-if="props.mode === MultiselectMode.TAGS" #tag="{ option, handleTagRemove }">
					<Chip
						:key="option.value"
						:label="option.label"
						:count="option.count"
						@remove="(e: Event) => handleTagRemove(option, e)"
					/>
				</template>

				<template #clear="{ clear }">
					<i
						class="ClearIcon lnr-cross"
						aria-hidden="true"
						@mousedown.prevent.stop="clear"
					></i>
				</template>
			</Multiselect>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, defineAsyncComponent } from 'vue';
const Multiselect = defineAsyncComponent(() => import('@vueform/multiselect'));
import SelectItem from '@components/SelectItem/SelectItem.vue';
import Chip from '@components/TagsElement/Chip.vue';
import { useMultiselect } from '@composables/useMultiselect';
import { multiselectPropsDefinition } from '@app/interfaces/useMultiselect.interface';
import { MultiselectMode } from '@app/enums/MultiselectMode';

const props = defineProps(multiselectPropsDefinition);


const emit = defineEmits<{
	'updateValues': [value: string | number | (string | number)[] | null];
	'update:modelValue': [value: string | number | (string | number)[] | null];
	'open': [];
	'close': [];
	'changeContinent': [];
}>();

const multiselectRef = ref<InstanceType<typeof Multiselect> | null>(null);

const {
	internalValue,
	formattedOptions,
	selectItemType,
	mapOptionToSelectItem
} = useMultiselect({ props, emit });

const openDropdown = async (): Promise<void> => {
	await nextTick();
	const multiselect = multiselectRef.value as any;
	if (multiselect && !multiselect.isOpen) {
		multiselect.open();
	}
};

onMounted(() => {
	openDropdown();
});
</script>


<style lang="scss">
.MobileModal {
	background: $White !important;
}
</style>

<style scoped lang="scss">
.MultiselectMobile {
	display: flex;
	background: $White;
	align-items: center;
	@include PaddingAreas(1);

	.BackButton {
		height: 2rem;
		width: 2rem;
		cursor: pointer;
		border-radius: 0.25rem;
		border: 1px solid $Grey-200;
		background: $White;

		@include PaddingAreas(0.5);
	}

	.Multiselect {
		width: 100%;

		@include MarginAreas(0, 0, 0, 0.5);

		.ContinentChange {
			display: flex;
			align-items: center;
			justify-content: space-between;
			color: $Grey-700;
			background: $Grey-100;
			border-top: 1px solid $Grey-200;
			border-bottom: 1px solid $Grey-200;

			@include TextStyle(HeadingExtraSmall);
			@include PaddingAreas(0.5, 1);

			.ChangeButton {
				color: $Blue-700;
				background: none;
				border: none;
				cursor: pointer;
				@include TextStyle(BodyMediumRegular);

				&:focus-visible {
					outline: 2px solid #{$Blue-600};
					border-radius: 0.25rem;
				}
			}
		}
	}

	:deep(.multiselect) {
		position: relative;
		min-height: auto;
		height: auto;
	}

	.MultiselectDropdown {
		border-radius: 0.5rem;
		--ms-font-size: 1rem;
		--ms-font-weight: 400;
		--ms-bg: #{$White};
		--ms-border-color: #{$Grey-300};
		--ms-border-width: 1px;
		--ms-radius: 0.5rem;
		--ms-ring-width: 0;
		--ms-ring-color: transparent;
		--ms-placeholder-color: #{$Grey-600};

		// Dropdown - always visible in mobile modal
		--ms-dropdown-bg: #{$White};
		--ms-dropdown-radius: 0;

		// Options
		--ms-option-font-size: 1rem;
		--ms-option-line-height: 1rem;
		--ms-option-bg-pointed: transparent;
		--ms-option-color-pointed: #{$Grey-900};
		--ms-option-bg-selected: transparent;
		--ms-option-bg-selected-pointed: transparent;
		--ms-option-color-selected: #{$Grey-900};
		--ms-option-color-selected-pointed: #{$Grey-900};
		--ms-option-py: 0.25rem;
		--ms-option-px: 0.5rem;

		@include PaddingAreas(0, 0.5);

		&.isAlwaysOpen {
			:deep(.multiselect-dropdown) {
				position: absolute;
				display: block !important;
				left: -2.5rem;
				max-height: none;
				border: none;
				box-shadow: none;
				bottom: -1.5rem;
				margin: 0 -1rem;
			}
		}

		&.is-active {
			outline: 2px solid #{$Blue-600};
		}
	}

	.SelectItemWrapper {
		width: 100%;
		pointer-events: none;

		:deep(.SelectItem) {
			max-width: 100%;
			@include PaddingAreas(0.5);
		}
	}

	:deep(.multiselect-option) {
		@include PaddingAreas(0, 0.5);

		&:last-child {
			border-bottom: none;
		}
	}

	:deep(.multiselect-options) {
		max-height: none !important;
	}

	&:not(:has(.ContinentChange)) :deep(.multiselect-options) {
		border-top: 1px solid $Grey-300;
	}

	:deep(.multiselect-placeholder) {
		@include PaddingAreas(0.25);
	}

	.MultiselectSingleLabel {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		position: absolute;
		left: 1rem;
		top: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;

		.Icon {
			width: 1rem;
			height: 1rem;
			flex-shrink: 0;
		}
	}

	.ClearIcon {
		color: $Grey-600;
		cursor: pointer;
		@include MarginAreas(0, 0.5, 0, 0);

		&:hover {
			color: $Grey-900;
		}
	}

	:deep(.multiselect-tags) {
		gap: 0.5rem;
		flex-wrap: wrap;
		padding: 0.75rem 0;
		margin: 0;
	}

	:deep(.multiselect-tags-search-wrapper) {
		width: 100%;
	}

	.MultiselectDescription {
		color: $Grey-700;
		@include TextStyle(BodyMediumRegular);
		@include MarginAreas(0, 0, 0.25, 3);
	}

	.MultiselectWarning {
		color: $Red-500;
		@include TextStyle(BodyMediumRegular);
		@include MarginAreas(0, 0, 0.25, 3);
	}
}
</style>
